// @flow
import * as React from 'react';
import { connect } from 'react-redux';

import Avatar from 'components/Avatar';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph';
import GroupAvatar from 'groups/components/GroupAvatar';
import GroupChatAvatar from 'chat/components/GroupChatAvatar';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import OrgAvatar from 'organizations/components/OrgAvatar';
import Text from 'components/Text';
import Tooltip from 'components/Tooltip';
import { RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import UserAvatar from 'users/components/UserAvatar';

type Props = {
  onClick: Function,

  // redux
  selection: Recipient[],
  results: Array<Recipient[]>,
};

class ComposeMessageRecipientsInput extends React.Component<Props, *> {
  renderToken = (
    recipientId: Backbone$Id,
    name: string,
    avatar: React$Element<*>,
    selectedRoles?: (?string)[]
  ) => {
    const tooltip = (
      <Tooltip>
        {name}
        {selectedRoles && selectedRoles[0] ? (
          <Text size="xxxsmall" color="wolf" element="div">
            {selectedRoles.join(', ')}
          </Text>
        ) : null}
      </Tooltip>
    );
    return (
      <LayerTrigger layer={tooltip} placement="top" alignment="middle" key={recipientId}>
        <span
          className="ComposeMessageRecipientsInput-token"
          data-test-selector={`split-composer-token-${recipientId}`}>
          <MediaObject center gutter="xsmall">
            {avatar}
            {selectedRoles && selectedRoles[0] ? (
              <React.Fragment>
                <Text className="ComposeMessageRecipientsInput-token-name" size="xsmall" truncate>
                  {name}
                </Text>
                <Glyph name="chevron-right" color="thunder" size="xxsmall" />
                <Text className="ComposeMessageRecipientsInput-token-roles" size="xsmall" truncate>
                  {selectedRoles.join(', ')}
                </Text>
              </React.Fragment>
            ) : (
              <Text className="ComposeMessageRecipientsInput-token-text" size="xsmall" truncate>
                {name}
              </Text>
            )}
          </MediaObject>
        </span>
      </LayerTrigger>
    );
  };

  render() {
    const { selection } = this.props;
    const truncatedSelection = selection.slice(0, 2);
    const overflowSelection = selection.slice(2);

    return (
      <div
        className="ComposeMessageRecipientsInput"
        onClick={() => {
          EventTracking.trackKey('announcements.share_to.input.click');
          this.props.onClick();
        }}
        data-test-selector="split-composer-recipients-input">
        <MediaObject
          center
          figure="right"
          gutter="xsmall"
          className="ComposeMessageRecipientsInput-inner">
          <MediaObject center gutter="xsmall">
            <Text size="xsmall" color="thunder">
              To:
            </Text>
            <MediaObject center gutter="xsmall">
              <React.Fragment>
                {truncatedSelection.length ? (
                  truncatedSelection.map(s => {
                    if (s.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
                      const orgName = s.organization.name;
                      const selectedFilters =
                        s.selectedFilters && s.selectedFilters.length
                          ? s.selectedFilters.map(selectedFilter => selectedFilter.value.label)
                          : [];
                      return this.renderToken(
                        s.id,
                        orgName,
                        <OrgAvatar size="xsmall" />,
                        selectedFilters
                      );
                    } else if (s.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
                      const { group } = s;
                      const selectedFilters =
                        s.selectedFilters && s.selectedFilters.length
                          ? s.selectedFilters.map(selectedFilter => selectedFilter.value.label)
                          : [];
                      return this.renderToken(
                        s.id,
                        group.className,
                        // $FlowFixMe - group is bad here because lack of type safety around it.
                        <GroupAvatar group={group} size="xsmall" />,
                        selectedFilters
                      );
                    } else if (s.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT) {
                      return this.renderToken(
                        s.id,
                        s.chat.getTitle(),
                        <GroupChatAvatar color="white" size="xsmall" />
                      );
                    } else if (s.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
                      const { member } = s;
                      return this.renderToken(
                        s.id,
                        member.getName(),
                        <UserAvatar
                          user={member.getUser().toJSON()}
                          size="xsmall"
                          color="white"
                          hideBadge
                        />
                      );
                    } else if (s.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT) {
                      const selectedFilters = [];
                      if (s.includeParents) {
                        selectedFilters.push('Parents');
                      }
                      if (s.includeStudents) {
                        selectedFilters.push('Students');
                      }
                      return this.renderToken(
                        s.id,
                        s.csvName,
                        <Avatar color="white" glyphName="spreadsheet" size="xsmall" />,
                        selectedFilters
                      );
                    }
                    return null;
                  })
                ) : (
                  <Text color="eclipse" size="xsmall">
                    Click to select recipients
                  </Text>
                )}
              </React.Fragment>
              {overflowSelection && overflowSelection.length ? (
                <Text size="xsmall" data-test="overflow">
                  {` + ${overflowSelection.length}`}
                </Text>
              ) : (
                <div />
              )}
            </MediaObject>
          </MediaObject>
          <Glyph
            className="ComposeMessageRecipientsInput-pencilIcon"
            name="pencil-stroke"
            color="thunder"
          />
        </MediaObject>
      </div>
    );
  }
}

const mapStateToProps = (state: Object) => {
  return {
    selection: state.roleBasedMessagingSearch.selection,
    results: state.roleBasedMessagingSearch.results,
  };
};

export { ComposeMessageRecipientsInput };

export default connect(mapStateToProps, undefined)(ComposeMessageRecipientsInput);
